import {CrosspointDataProvider, ViewportItem} from "@o2d/datasource";
import {html, nothing} from "lit";
import {classMap} from "lit/directives/class-map.js";
import {styleMap} from "lit/directives/style-map.js";
import {DateTime, Interval} from "luxon";
import {AreaConfiguration} from "@o2d/grid-layout";
import {Issue} from "../../domain/entities/issue.entity";
import {HeaderItem} from "../_shared/types/header-item.type";
import {buildHeaderItemStyleMap} from "../_shared/header-item-style.helper";
import type {ResourcePlanningItem} from "./types/resource-planning-item.type";
import {resolveAllEntityColorBars} from "../../helpers/color-bar.resolver";
import {buildAllEntitiesStyleMap} from "../../helpers/entity-color-style.helper";
import {createColorFilterControlAreaConfiguration, isInPast} from "../_shared/timeline-area.factory";
import {rootUidOf} from "../../domain/helpers/issue-type-root.helper";

/**
 * Creates area configurations for the resource planning view.
 * Identical to standard timeline areas for headers, but uses a custom
 * content cellRenderer that applies resource-planning-specific CSS classes
 * and renders a time budget badge.
 *
 * `grid-area-control1` (the entity-color toggle button + its color-filter
 * radio group) is the exact same, shared area config the standard timeline
 * views use (see createColorFilterControlAreaConfiguration's own docstring)
 * — reused here verbatim rather than reimplemented, so it stays a single
 * SRP-scoped unit instead of two independently-maintained copies.
 */
export const createResourcePlanningAreaConfigurations = (
    isHighlighted: (uid: string) => boolean,
    isFilteredOut: (issue: Issue) => boolean = () => false
): AreaConfiguration[] => [
    {
        // Row header: Entity name with background color
        areaId: 'grid-area-0',
        sectionIds: {
            columnSectionId: 'column-section-0',
            rowSectionId: 'row-section-4'
        },
        classes: ["y-axis", "y-axis-title"],
        cellRenderer: (item: ViewportItem<unknown>) => {
            const data = item.data as HeaderItem;
            return html`<span class="employee-name" style=${styleMap(buildHeaderItemStyleMap(data.color))}>${data.title}</span>`;
        }
    },
    {
        // Row header: Highlight toggle button
        areaId: 'grid-area-1',
        sectionIds: {
            columnSectionId: 'column-section-1',
            rowSectionId: 'row-section-4'
        },
        classes: ["y-axis"],
        cellRenderer: (item: ViewportItem<unknown>) => {
            const data = item.data as HeaderItem;
            const uid = data.entities?.[0]?.uid ?? '';
            if (isHighlighted(uid)) {
                return html`<div class="btn btn__highlight active" data-role="highlight" data-entity-uid="${uid}"><i class="icon icon-highlighter"></i></div>`;
            }
            return html`<div class="btn btn__highlight" data-role="highlight" data-entity-uid="${uid}"><i class="icon icon-highlighter"></i></div>`;
        }
    },
    {
        // Row header: Context menu button
        areaId: 'grid-area-2',
        sectionIds: {
            columnSectionId: 'column-section-2',
            rowSectionId: 'row-section-4'
        },
        classes: ["y-axis"],
        cellRenderer: (item: ViewportItem<unknown>) => {
            const data = item.data as HeaderItem;
            const uid = data.entities?.[0]?.uid ?? '';
            return html`<div class="btn" data-role="context-menu" data-entity-uid="${uid}">&#x22EE;</div>`;
        }
    },
    {
        // X-axis header: Month/Year
        areaId: 'grid-area-3',
        sectionIds: {
            columnSectionId: 'column-section-3',
            rowSectionId: 'row-section-0'
        },
        classes: ["x-axis"],
        cellRenderer: (item: ViewportItem<unknown>, dataProvider: CrosspointDataProvider<unknown, unknown, unknown>) => {
            const [minIndex] = item.visibleIndexRangeX;
            const startDate = String(dataProvider.axisX.getValue(minIndex));
            if (!startDate) return html``;
            const dt = DateTime.fromISO(startDate);
            const monthName = dt.toFormat('LLLL', {locale: 'de'});
            return html`<span class="month-header">${monthName} ${dt.year}</span>`;
        }
    },
    {
        // X-axis header: Calendar week
        areaId: 'grid-area-4',
        sectionIds: {
            columnSectionId: 'column-section-3',
            rowSectionId: 'row-section-1'
        },
        classes: ["x-axis"],
        cellRenderer: (item: ViewportItem<unknown>, dataProvider: CrosspointDataProvider<unknown, unknown, unknown>) => {
            const [minIndex, maxIndex] = item.visibleIndexRangeX;
            const startDate = dataProvider.axisX.getValue(minIndex) as string;
            const endDate = dataProvider.axisX.getValue(maxIndex) as string;
            if (!startDate || !endDate) return html``;
            const visibleInterval = Interval.fromDateTimes(
                DateTime.fromISO(startDate),
                DateTime.fromISO(endDate).plus({days: 1})
            );
            const weeks = visibleInterval.splitBy({weeks: 1}).map(weekInterval =>
                html`<span class="week-header">KW ${weekInterval.start!.localWeekNumber}</span>`
            );
            return html`${weeks}`;
        }
    },
    {
        // X-axis header: Weekday
        areaId: 'grid-area-5',
        sectionIds: {
            columnSectionId: 'column-section-3',
            rowSectionId: 'row-section-2'
        },
        classes: ["x-axis"],
        cellRenderer: (item: ViewportItem<unknown>, dataProvider: CrosspointDataProvider<unknown, unknown, unknown>) => {
            const [minIndex, maxIndex] = item.visibleIndexRangeX;
            const startDate = dataProvider.axisX.getValue(minIndex) as string;
            const endDate = dataProvider.axisX.getValue(maxIndex) as string;
            if (!startDate || !endDate) return html``;
            const visibleInterval = Interval.fromDateTimes(
                DateTime.fromISO(startDate),
                DateTime.fromISO(endDate).plus({days: 1})
            );
            const days = visibleInterval.splitBy({day: 1}).map(dayInterval => {
                const isWeekend = dayInterval.start!.weekday >= 6;
                const isToday = dayInterval.start!.hasSame(DateTime.now(), 'day');
                const classes = ['weekday-header', isWeekend && 'weekend', isToday && 'today'].filter(Boolean).join(' ');
                return html`<span class="${classes}">${dayInterval.start!.weekdayShort}</span>`;
            });
            return html`${days}`;
        }
    },
    {
        // X-axis header: Day of month
        areaId: 'grid-area-6',
        sectionIds: {
            columnSectionId: 'column-section-3',
            rowSectionId: 'row-section-3'
        },
        classes: ["x-axis"],
        cellRenderer: (item: ViewportItem<unknown>, dataProvider: CrosspointDataProvider<unknown, unknown, unknown>) => {
            const [minIndex, maxIndex] = item.visibleIndexRangeX;
            const startDate = dataProvider.axisX.getValue(minIndex) as string;
            const endDate = dataProvider.axisX.getValue(maxIndex) as string;
            if (!startDate || !endDate) return html``;
            const visibleInterval = Interval.fromDateTimes(
                DateTime.fromISO(startDate),
                DateTime.fromISO(endDate).plus({days: 1})
            );
            const days = visibleInterval.splitBy({day: 1}).map(dayInterval => {
                const isWeekend = dayInterval.start!.weekday >= 6;
                const isToday = dayInterval.start!.hasSame(DateTime.now(), 'day');
                const classes = ['day-header', isWeekend && 'weekend', isToday && 'today'].filter(Boolean).join(' ');
                return html`<span class="${classes}">${dayInterval.start!.day}</span>`;
            });
            return html`${days}`;
        }
    },
    {
        // Content area: Resource planning issues with scheduling metadata
        areaId: 'grid-area-7',
        sectionIds: {
            columnSectionId: 'column-section-3',
            rowSectionId: 'row-section-4'
        },
        enableClustering: true,
        showBackgroundGrid: true,
        enableCellClickEvent: true,
        enableCellHoverEvent: true,
        rowUnderlayRenderer: (_yIndex: number, yValue: unknown) => {
            const uid = yValue as string;
            if (!isHighlighted(uid)) return null;
            return {classes: {highlighted: true}};
        },
        columnUnderlayRenderer: (_xIndex: number, xValue: unknown) => {
            const date = DateTime.fromISO(xValue as string);
            const classes: Record<string, boolean> = {};
            if (date.weekday >= 6) classes['weekend'] = true;
            if (date.hasSame(DateTime.now(), 'day')) classes['today'] = true;
            return Object.keys(classes).length > 0 ? {classes} : null;
        },
        cellRenderer: (item: ViewportItem<unknown>) => {
            const data = item.data as ResourcePlanningItem;
            const meta = data.meta;

            const classes = {
                'timeline-issue': true,
                'issue--fixed': meta?.isFixed ?? false,
                'is-overdue': meta?.isOverdue ?? false,
                'is-in-past': isInPast(data.issue.end),
                'issue_first-day': true,
                'issue_last-day': true,
                'filtered-out': isFilteredOut(data.issue)
            };

            // Anke, 2026-09-15: was `meta.timeBudgetRemainingAtSlot` — the
            // employee's LEFTOVER day capacity after this ticket, not a
            // value about the ticket itself ("nicht wichtig zu sehen").
            // Swapped for the per-assignee time estimate actually booked
            // against this ticket for this employee ("die Zeit, die der
            // Bearbeiter für dieses Ticket braucht"). Read from `meta.
            // timeBudget`, computed server-side (ResourcesViewportQueryService
            // ::getTimeBudgetForEmployee) — NOT from `data.issue.
            // employeeToIssueAssignments`: that Issue entity is loaded
            // cache-first via `IssueRepository.findById()`
            // (resources-crosspoint-data.service.ts), which never requests
            // the `employeeToIssueAssignments` include, so a cold-loaded
            // Issue's assignments would silently be `[]` (Review 0037-01,
            // Linse A, MAJOR).
            const budgetMinutes = meta?.timeBudget ?? 0;
            const budgetHours = Math.round(budgetMinutes / 60 * 10) / 10;

            // Same entity-color-bar rendering as the standard timeline views
            // (see grid-area-7 in timeline-area.factory.ts) instead of a fixed
            // single-color background — the shared control-area-1/2 color-
            // filter radio group (createColorFilterControlAreaConfiguration)
            // only has a visible effect if .timeline-issue's background is
            // built from these per-entity CSS custom properties, since the
            // radios' show-color-<kind> classes on #grid-area-7 (timeline.css)
            // only ever set --issue-color-N, nothing else.
            const barsByKind = resolveAllEntityColorBars(data.issue);

            return html`<div
                class=${classMap(classes)}
                data-sid=${data.issue.uid}
                data-status=${data.issue.status?.key ?? nothing}
                data-root-type=${rootUidOf(data.issue.type) ?? nothing}
                data-abbreviation-type-1=${barsByKind.type[0]?.shortcut ?? nothing}
                style=${styleMap(buildAllEntitiesStyleMap(barsByKind))}>
                <span>${data.issue.type.shortcut}</span>
                <span>${data.issue.title}</span>
                ${budgetMinutes > 0 ? html`<span class="time-budget-badge">${budgetHours}h</span>` : html``}
            </div>`;
        }
    },
    createColorFilterControlAreaConfiguration()
];
